import React from 'react';
import { FormattedMessage } from 'react-intl';
import { ofType } from 'redux-observable';
import { concat, EMPTY, of, Subject, zip, race } from 'rxjs';
import { take, mergeMap, catchError, filter, switchMap } from 'rxjs/operators';

import { ModalService } from 'ui-components/services/modal-service';
import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_INFO } from 'ui-components/services/notification-service';
import { NotificationHubService } from '../notification-hub/notification-hub-service';
import { NotificationType } from '../notification-hub/notification-hub.types';
import { notificationDescriptionMessages } from '../notification-hub/notification-messages';

import { productGridReloadSubject } from './grid/product-grid.observables';
import * as GridActions from './grid/product.grid.actions';
import { processBulkLoadResponse } from './product-bulk-load-processor';
import { patchProductEntitiesWithProductWarehouseUpdates } from './product-warehouse-processor';
import * as Actions from './products.actions';
import * as ProductsApi from './products.api';
import { GenericErrorRetryContainer } from '../../containers/generic-error-retry-container';
import { emptyObject } from 'ui-components/default-props/empty';
import { ALIAS_MODAL_TYPE, PRODUCT_MODAL_TYPE } from '../../pages/products-page/products/constants/import-products';
import { ProductDetails } from '../../products/product-details';
import { mapShipFromToProductWarehouse } from '../../products/product-details/mapping-helpers';
import { messages } from '../../products/product-details/messages';
import { DocumentService } from '../../services/document-service';
import { createLookupHash } from '../../services/hash-service';
import { ProductDetailsService } from '../../services/product-details.service';
import { Workflows } from '../../tracking/tracking-constants';
import { WorkflowSteps, trackWorkflow } from '../../tracking/use-workflow-tracking';
import { initShipmentConfiguration } from '../csw/csw.actions';
import { entitiesLoad } from '../entities/entities.actions';
import Global from '../global-selectors';
import * as InventoryActions from '../inventory/inventory.actions';
import { GET_ORDERS_FAILURE, GET_ORDERS_SUCCESS, silentRequestGetOrders } from '../orders/orders.actions';
import { startProductImportAndRespond } from '../pusher/pusher.product-import.observables';

export const bulkLoadProductDataRequest = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.BULK_LOAD_PRODUCT_DATA),
    switchMap((action) => {
      const { productIds } = action;
      const state = getState();
      const entityProducts = Global.selectEntitiesProductsByIds(state, productIds) || emptyObject;
      const entityProductIds = Object.keys(entityProducts);
      // list of productIds not in entities already
      const missingProductIds = productIds.filter((p) => !entityProductIds.includes(p));
      return ProductsApi.bulkLoadProductsAsObservable(missingProductIds).pipe(
        mergeMap((response) => {
          const entitiesToLoad = {
            entities: {
              products: createLookupHash(processBulkLoadResponse(response), 'productId'),
            },
          };
          return of(entitiesLoad(entitiesToLoad));
        }),
      );
    }),
  );
};

export const productShippingDefaultsSavedSubject = new Subject();

export const reloadUpdatedProductInformation = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.FORCE_BULK_LOAD_PRODUCT_DATA),
    switchMap((action) => {
      const { productIds } = action;
      const state = getState();
      productShippingDefaultsSavedSubject.next({ didSaveDefaults: true });

      return ProductsApi.bulkLoadProductsAsObservable(productIds).pipe(
        mergeMap((response) => {
          const entitiesToLoad = {
            entities: {
              ...state.entities,
              products: createLookupHash(processBulkLoadResponse(response), 'productId'),
            },
          };
          return of(entitiesLoad(entitiesToLoad));
        }),
      );
    }),
  );
};

export const openProductDetailModal = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.PRODUCT_DETAILS_MODAL_OPEN),
    mergeMap(({ productId }) => {
      const productDetailsModalId = ModalService.launchModal({
        component: ProductDetails,
      });

      return concat(
        ProductDetailsService.fetchProductDetails(getState(), productId, productDetailsModalId),
        ProductDetailsService.fetchProductCategories(productDetailsModalId),
      );
    }),
  );
};

export const onProductCreate = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.CREATE_PRODUCT),
    mergeMap((action) => {
      const { product, updateOptions } = action.payload;
      return ProductsApi.createProduct(product, updateOptions).pipe(
        mergeMap((response) => {
          const { modalId } = action.payload;
          const { reuseNewProductModal, onSuccessCallback } = updateOptions;
          const { productId } = response[0];

          productGridReloadSubject.next();

          if (onSuccessCallback) {
            onSuccessCallback();
          }

          if (reuseNewProductModal) {
            return concat(
              ProductDetailsService.getProductDetailsObservable(getState(), productId, modalId),
              ProductDetailsService.fetchProductCategories(modalId),
            );
          }
          ModalService.closeModalById(modalId);
          return of(action.meta.onSuccess(), Actions.bulkLoadProductData([product.productId]));
        }),
        catchError((e) => {
          const errorsList = e && e.response && e.response.data && e.response.data.Errors ? e.response.data.Errors : [];
          ProductDetailsService.handleProductUpdateErrors(errorsList);
          return of(action.meta.onError(e));
        }),
      );
    }),
  );
};

export const onProductUpdate = (action$) => {
  return action$.pipe(
    ofType(Actions.UPDATE_PRODUCT),
    mergeMap((action) => {
      const { product, updateOptions } = action.payload;
      return ProductsApi.updateProduct(product, updateOptions).pipe(
        mergeMap(() => {
          const { modalId } = action.payload;
          const { onSuccessCallback } = updateOptions;
          const arrayOfActions = [];

          ModalService.closeModalById(modalId);

          if (onSuccessCallback) {
            onSuccessCallback();
          }

          if (updateOptions.shouldReloadProductsGrid) {
            productGridReloadSubject.next();
          }

          if (updateOptions.shouldReloadInventoryGrid) {
            arrayOfActions.push(of(InventoryActions.reloadInventoryGrid(updateOptions?.pageData)));
          }

          return concat(of(action.meta.onSuccess()), of(Actions.bulkLoadProductData([product.productId])), ...arrayOfActions);
        }),
        catchError((e) => {
          const errorsList = e && e.response && e.response.data && e.response.data.Errors ? e.response.data.Errors : [];
          ProductDetailsService.handleProductUpdateErrors(errorsList);
          return of(action.meta.onError(e));
        }),
      );
    }),
  );
};

export const onApplyProductUpdatesToOpenFulfillmentPlansSuccess = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.APPLY_PRODUCT_UPDATE_TO_OPEN_FULFILLMENTPLANS_SUCCESS),
    mergeMap((action) => {
      const { dataLoadSuccessNotificationConfig, dataLoadFailureNotificationConfig } = action.payload;
      const state = getState();
      const [selectedFulfillmentPlanId] = Global.selectSelectedOrdersIds(state);

      const concatArray = [of(silentRequestGetOrders())];

      if (selectedFulfillmentPlanId) {
        const cswReloadObservable = race(
          zip(
            action$.pipe(
              ofType(GET_ORDERS_SUCCESS),
              take(1),
              mergeMap(() => {
                if (dataLoadSuccessNotificationConfig) {
                  NotificationService.showNotification(dataLoadSuccessNotificationConfig);
                }

                return of(initShipmentConfiguration(selectedFulfillmentPlanId));
              }),
            ),
            action$.pipe(
              ofType(GET_ORDERS_FAILURE),
              take(1),
              mergeMap(() => {
                if (dataLoadFailureNotificationConfig) {
                  NotificationService.showNotification(dataLoadFailureNotificationConfig);
                }
                return EMPTY;
              }),
            ),
          ),
        );
        concatArray.push(cswReloadObservable);
      }

      return concat(...concatArray);
    }),
  );
};

export const getProducts = (action$) => {
  return action$.pipe(
    ofType(Actions.GET_PRODUCTS),
    mergeMap(() => {
      return ProductsApi.getProducts().pipe(
        mergeMap((response) => {
          const entitiesToLoad = {
            entities: {
              products: createLookupHash(response.products, 'productId'),
            },
          };
          return of(entitiesLoad(entitiesToLoad));
        }),
        catchError(() => {
          // TODO: handle error case
          return EMPTY;
        }),
      );
    }),
  );
};

export const getProductProfilesSuccess = (action$) =>
  action$.pipe(
    ofType(Actions.GET_PRODUCT_PROFILES_SUCCESS),
    mergeMap((action) => {
      const { productProfiles } = action.payload;
      const entitiesToLoad = {
        entities: {
          productProfiles: createLookupHash(productProfiles || [], 'productProfileId'),
        },
      };
      return of(entitiesLoad(entitiesToLoad));
    }),
  );

export const getProductCategoriesSuccess = (action$) =>
  action$.pipe(
    ofType(Actions.GET_PRODUCT_CATEGORIES_SUCCESS),
    mergeMap((action) => {
      const { productCategories } = action.payload;
      const entitiesToLoad = {
        entities: {
          productCategories: createLookupHash(productCategories || [], 'categoryId'),
        },
      };
      return of(entitiesLoad(entitiesToLoad));
    }),
  );

export const addWarehouseToProducts = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.ADD_WAREHOUSE_TO_PRODUCTS),
    mergeMap((action) => {
      const { productIds, warehouseId } = action;
      return ProductsApi.addProductWarehouse({ productIds, warehouseId }).pipe(
        mergeMap(() => {
          const state = getState();
          const productEntities = Global.selectEntitiesProductsByIds(state, {
            productIds,
          });
          const sellerShipFroms = Global.selectSellerShipFroms(state);
          const patchedEntities = productEntities.map((ent) => {
            const productWarehouses = sellerShipFroms.map((sf) => mapShipFromToProductWarehouse(sf, [...new Set(ent.productWarehouses)]));
            const newAssociations = productWarehouses.map((productWarehouse) => {
              if (productWarehouse.warehouseId.toString() === warehouseId.toString()) {
                // oxlint-disable-next-line no-param-reassign
                productWarehouse.isSelected = true;
              }
              return productWarehouse;
            });
            return { ...ent, productWarehouses: [...newAssociations] };
          });
          const entitiesToLoad = {
            entities: {
              products: createLookupHash(patchedEntities || [], 'productId'),
            },
          };
          return of(entitiesLoad(entitiesToLoad));
        }),
        catchError(() => {
          ModalService.launchModal({
            component: GenericErrorRetryContainer,
            props: {
              action,
              title: <FormattedMessage {...messages.addWarehousesToProductErrorTitle} />,
              message: <FormattedMessage {...messages.addWarehousesToProductErrorMessage} />,
            },
          });
          return EMPTY;
        }),
      );
    }),
  );
};

export const removeWarehouseFromProducts = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.REMOVE_WAREHOUSE_FROM_PRODUCTS),
    mergeMap((action) => {
      const { productIds, warehouseId } = action;

      return ProductsApi.removeProductWarehouse({
        productIds,
        warehouseId,
      }).pipe(
        mergeMap(() => {
          const state = getState();
          const productEntities = Global.selectEntitiesProductsByIds(state, {
            productIds,
          });
          const patchedEntities = productEntities.map((ent) => {
            const productWarehouses = [...new Set(ent.productWarehouses)];
            const newAssociations = productWarehouses.map((productWarehouse) => {
              if (productWarehouse.warehouseId.toString() === warehouseId.toString()) {
                // oxlint-disable-next-line no-param-reassign
                productWarehouse.isSelected = false;
              }
              return productWarehouse;
            });
            return { ...ent, productWarehouses: [...newAssociations] };
          });
          const entitiesToLoad = {
            entities: {
              products: createLookupHash(patchedEntities || [], 'productId'),
            },
          };
          return of(entitiesLoad(entitiesToLoad));
        }),
        catchError(() => {
          ModalService.launchModal({
            component: GenericErrorRetryContainer,
            props: {
              action,
              title: <FormattedMessage {...messages.addWarehousesToProductErrorTitle} />,
              message: <FormattedMessage {...messages.addWarehousesToProductErrorMessage} />,
            },
          });
          return EMPTY;
        }),
      );
    }),
  );
};

export const updateProductWarehouseAssociations = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.UPDATE_PRODUCT_WAREHOUSE_ASSOCIATIONS),
    mergeMap((action) => {
      const { productIds, warehouseIdsToAdd, warehouseIdsToDelete } = action;
      return ProductsApi.bulkUpdateProductWarehouseAssociations({
        productIds,
        warehouseIdsToAdd,
        warehouseIdsToDelete,
      }).pipe(
        mergeMap(() => {
          const state = getState();

          const patchedEntities = patchProductEntitiesWithProductWarehouseUpdates(
            state,
            productIds,
            warehouseIdsToAdd,
            warehouseIdsToDelete,
          );

          const entitiesToLoad = {
            entities: {
              products: createLookupHash(patchedEntities || [], 'productId'),
            },
          };
          return of(entitiesLoad(entitiesToLoad));
        }),
        catchError(() => {
          ModalService.launchModal({
            component: GenericErrorRetryContainer,
            props: {
              action,
              title: <FormattedMessage {...messages.addWarehousesToProductErrorTitle} />,
              message: <FormattedMessage {...messages.addWarehousesToProductErrorMessage} />,
            },
          });
          return EMPTY;
        }),
      );
    }),
  );
};

export const addTagsToProducts = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.ADD_TAGS_TO_PRODUCTS),
    mergeMap((action) => {
      const { productIds, tagId } = action;

      return ProductsApi.addProductTag({ productIds, tagId }).pipe(
        mergeMap(() => {
          const state = getState();
          const productEntities = Global.selectEntitiesProductsByIds(state, {
            productIds,
          });
          const patchedEntities = productEntities.map((ent) => {
            const tags = new Set(ent.tags);
            tags.add(tagId);
            return { ...ent, tags: [...tags] };
          });
          const entitiesToLoad = {
            entities: {
              products: createLookupHash(patchedEntities || [], 'productId'),
            },
          };
          return of(entitiesLoad(entitiesToLoad));
        }),
        catchError(() => {
          ModalService.launchModal({
            component: GenericErrorRetryContainer,
            props: {
              action,
              title: <FormattedMessage {...messages.addTagsToProductErrorTitle} />,
              message: <FormattedMessage {...messages.addTagsToProductErrorMessage} />,
            },
          });
          return EMPTY;
        }),
      );
    }),
  );
};

export const removeTagsFromProducts = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.REMOVE_TAGS_FROM_PRODUCTS),
    mergeMap((action) => {
      const { productIds, tagId } = action;

      return ProductsApi.removeProductTag({ productIds, tagId }).pipe(
        mergeMap(() => {
          const state = getState();
          const productEntities = Global.selectEntitiesProductsByIds(state, {
            productIds,
          });
          const patchedEntities = productEntities.map((ent) => {
            const tags = new Set(ent.tags);
            tags.delete(tagId);
            return { ...ent, tags: [...tags] };
          });
          const entitiesToLoad = {
            entities: {
              products: createLookupHash(patchedEntities || [], 'productId'),
            },
          };
          return of(entitiesLoad(entitiesToLoad));
        }),
        catchError(() => {
          ModalService.launchModal({
            component: GenericErrorRetryContainer,
            props: {
              action,
              title: <FormattedMessage {...messages.addTagsToProductErrorTitle} />,
              message: <FormattedMessage {...messages.addTagsToProductErrorMessage} />,
            },
          });
          return EMPTY;
        }),
      );
    }),
  );
};

export const exportProductsRequest = (action$, { getState }) => {
  return action$.pipe(
    ofType(GridActions.EXPORT_PRODUCTS_REQUEST),
    mergeMap((action) => {
      const { searchTerm, appliedFilters, productIds } = action;
      const state = getState();
      const currentUserId = Global.selectUserCurrentUserId(state);
      return ProductsApi.startProductExport(searchTerm, appliedFilters, productIds).pipe(
        mergeMap(() => {
          const exportComplete$ = action$.pipe(
            ofType(GridActions.EXPORT_PRODUCTS_COMPLETE),
            filter((data) => data?.data?.Notification?.UserID === currentUserId),
            take(1),
            mergeMap((data) => {
              const message = data?.data?.Notification?.Message;
              const checkStart = '<a href="https:';
              const indexOfStart = message.indexOf(checkStart) + checkStart.length;
              const indexOfEnd = message.indexOf('">Click here to view') - 1;
              const downloadUrl = message.substring(indexOfStart, indexOfEnd);
              DocumentService.downloadFile(downloadUrl);
              NotificationHubService.showNotification(
                {
                  notificationType: NotificationType.PRODUCT_EXPORT_COMPLETED,
                  defaultTitle: messages.productExportCompleteTitle.defaultMessage,
                  defaultDescription: notificationDescriptionMessages.PRODUCT_EXPORT_COMPLETED.defaultMessage,
                  metadataJson: JSON.stringify({ downloadUrl }),
                },
                {
                  notificationType: NOTIFICATION_TYPE_INFO,
                  title: <FormattedMessage {...messages.productExportCompleteTitle} />,
                  content: <FormattedMessage {...messages.productExportCompleteBody} />,
                },
              );
              return of(Actions.getProductExportSuccess());
            }),
          );
          return exportComplete$;
        }),
        catchError(() => {
          NotificationHubService.showNotification(
            {
              notificationType: NotificationType.PRODUCT_EXPORT_FAILED,
              defaultTitle: messages.errorNotificationTitle.defaultMessage,
              defaultDescription: messages.errorNotificationMessageProductExport.defaultMessage,
            },
            {
              notificationType: NOTIFICATION_TYPE_ERROR,
              title: <FormattedMessage {...messages.errorNotificationTitle} />,
              content: <FormattedMessage {...messages.errorNotificationMessageProductExport} />,
            },
          );
          return EMPTY;
        }),
      );
    }),
  );
};

export const importProductsRequest = (action$) => {
  return action$.pipe(
    ofType(GridActions.IMPORT_PRODUCTS_REQUEST),
    mergeMap((action) => {
      const { data } = action;

      return startProductImportAndRespond(() => {
        return ProductsApi.startProductImport(data);
      }).pipe(
        mergeMap(({ findMessage$ }) => {
          return findMessage$.pipe(
            mergeMap(({ response }) => {
              trackWorkflow(Workflows.PRODUCT_IMPORT_FLOW, WorkflowSteps.COMPLETE, {
                fileName: data.file.name,
                identifyBy: data.identifyBy,
                overwriteExisting: data.overwriteExisting,
              });

              const importErrors = response.Errors || [];
              const failedCount = importErrors.length;

              if (data.importType === PRODUCT_MODAL_TYPE) {
                NotificationHubService.showNotification(
                  {
                    notificationType: NotificationType.PRODUCT_IMPORT_COMPLETED,
                    defaultTitle: messages.productImportCompleteNotificationContent.defaultMessage,
                    defaultDescription: `Your product import finished successfully.${failedCount > 0 ? ` ${failedCount} products failed to import.` : ''}`,
                    metadataJson: JSON.stringify({ failedCount }),
                  },
                  {
                    content: <FormattedMessage {...messages.productImportCompleteNotificationContent} />,
                  },
                );
              } else if (data.importType === ALIAS_MODAL_TYPE) {
                NotificationHubService.showNotification(
                  {
                    notificationType: NotificationType.PRODUCT_ALIAS_IMPORT_COMPLETED,
                    defaultTitle: messages.productAliasImportCompleteNotificationContent.defaultMessage,
                    defaultDescription: `Your product alias import finished successfully.${failedCount > 0 ? ` ${failedCount} aliases failed to import.` : ''}`,
                    metadataJson: JSON.stringify({ failedCount }),
                  },
                  {
                    content: <FormattedMessage {...messages.productAliasImportCompleteNotificationContent} />,
                  },
                );
              } else {
                NotificationHubService.showNotification(
                  {
                    notificationType: NotificationType.PRODUCT_BUNDLES_IMPORT_COMPLETED,
                    defaultTitle: messages.productBundlesImportCompleteNotificationContent.defaultMessage,
                    defaultDescription: `Your product bundles import finished successfully.${failedCount > 0 ? ` ${failedCount} bundles failed to import.` : ''}`,
                    metadataJson: JSON.stringify({ failedCount }),
                  },
                  {
                    content: <FormattedMessage {...messages.productBundlesImportCompleteNotificationContent} />,
                  },
                );
              }

              // presence of Success on the response means it's new pusher message style. Absence of it entirely means legacy style
              if (response.Success !== undefined) {
                return response.Success ? of(Actions.getProductImportSuccess()) : of(Actions.getProductImportLogSuccess(importErrors));
              }

              // deprecated, clean up API call in SPD-12666 once flag has been wide for a while
              return of(Actions.getProductImportLogFromJobGuid(response.jobId));
            }),
          );
        }),
        catchError((e) => {
          trackWorkflow(Workflows.PRODUCT_IMPORT_FLOW, WorkflowSteps.ERROR, {
            status: e.response.status,
            statusText: e.response.statusText,
            errors: e.response.data.Errors,
          });

          NotificationHubService.showNotification(
            {
              notificationType: NotificationType.PRODUCT_IMPORT_FAILED,
              defaultTitle: messages.errorNotificationTitleProductImport.defaultMessage,
              defaultDescription: messages.errorNotificationMessageProductImport.defaultMessage,
            },
            {
              notificationType: NOTIFICATION_TYPE_ERROR,
              title: <FormattedMessage {...messages.errorNotificationTitleProductImport} />,
              content: <FormattedMessage {...messages.errorNotificationMessageProductImport} />,
            },
          );
          return EMPTY;
        }),
      );
    }),
  );
};

export const getProductImportLog = (action$) =>
  action$.pipe(
    ofType(Actions.GET_PRODUCT_IMPORT_LOG),
    mergeMap((action) =>
      ProductsApi.getImportLogFromJobGUID(action.importJobGuid).pipe(
        mergeMap((response) => {
          if (response.length > 1) {
            return of(Actions.getProductImportLogSuccess(response));
          }
          return EMPTY;
        }),
        catchError(() => {
          NotificationHubService.showNotification(
            {
              notificationType: NotificationType.PRODUCT_IMPORT_FAILED,
              defaultTitle: messages.errorNotificationTitleProductImport.defaultMessage,
              defaultDescription: messages.errorNotificationMessageProductImport.defaultMessage,
            },
            {
              notificationType: NOTIFICATION_TYPE_ERROR,
              title: <FormattedMessage {...messages.errorNotificationTitleProductImport} />,
              content: <FormattedMessage {...messages.errorNotificationMessageProductImport} />,
            },
          );
          return EMPTY;
        }),
      ),
    ),
  );
